/**
 * The signed in layout, Kylon style: dark sidebar (rooms with unread counts,
 * direct messages, work, settings), the thread in the middle, room info on the
 * right. On a phone the sidebar becomes a drawer.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { HelpPanel, useHelpKey } from "../components/Help";
import { WorkDrawer } from "../components/WorkDrawer";
import { Avatar, Badge, ErrorNote, Modal, RoomDot } from "../components/ui";
import { ApiError } from "../lib/api";
import { createRoom, EMPTY, joinRoom, loadBootstrap, openDm, roomTitle, signOut, useStore, type Room } from "../lib/store";
import { NewProjectModal } from "./projects";

function Lock() {
  return (
    <svg className="lock" width="11" height="11" viewBox="0 0 16 16" aria-label="private" role="img">
      <path fill="currentColor" d="M5 7V5a3 3 0 1 1 6 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1Zm1.5 0h3V5a1.5 1.5 0 1 0-3 0v2Z" />
    </svg>
  );
}

export function AppShell() {
  const status = useStore((s) => s.status);
  const boot = useStore((s) => s.boot);
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const location = useLocation();
  const openHelp = useCallback(() => setHelp(true), []);
  useHelpKey(openHelp);

  useEffect(() => {
    void loadBootstrap();
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  const totalUnread = boot?.rooms.filter((r) => r.isMember).reduce((n, r) => n + r.unread, 0) ?? 0;
  useEffect(() => {
    document.title = totalUnread ? `(${totalUnread}) Agora` : "Agora";
  }, [totalUnread]);

  if (status === "signedOut") return <Navigate to="/sign-in" replace />;
  if (status === "noWorkspace") return <NoWorkspace />;
  if (!boot) return <div className="loading" aria-busy="true" />;

  return (
    <div className={`shell${open ? " open" : ""}`}>
      <Sidebar onHelp={openHelp} />
      {help && <HelpPanel onClose={() => setHelp(false)} />}
      <div className="scrim" onClick={() => setOpen(false)} />
      <div className="content">
        <button className="menu-btn" type="button" onClick={() => setOpen(true)} aria-label="Open the menu">
          <span />
          <span />
          <span />
        </button>
        <Outlet />
      </div>
      {boot.workspace.role === "admin" && <WorkDrawer />}
    </div>
  );
}

function NoWorkspace() {
  return (
    <main className="auth">
      <section className="card">
        <h1>Almost there</h1>
        <p className="lead">Please confirm your email or ask an admin of your team for an invite.</p>
        <button className="btn ghost" type="button" onClick={() => void signOut()}>
          Sign out
        </button>
      </section>
    </main>
  );
}

function Sidebar({ onHelp }: { onHelp: () => void }) {
  const boot = useStore((s) => s.boot)!;
  const state = useStore((s) => s);
  const navigate = useNavigate();
  const [modal, setModal] = useState<null | "room" | "dm" | "browse" | "project">(null);

  const joined = boot.rooms.filter((r) => r.kind === "room" && r.isMember).sort((a, b) => a.name.localeCompare(b.name));
  const browsable = boot.rooms.filter((r) => r.kind === "room" && !r.isMember);
  const dms = boot.rooms.filter((r) => r.kind === "dm").sort((a, b) => b.lastMessageId - a.lastMessageId);
  const guest = boot.workspace.role === "guest";

  return (
    <aside className="side" aria-label="Agora menu">
      <div className="side-top">
        <div className="ws">
          {boot.workspace.name}
          <small>{state.live ? "Connected" : "Reconnecting"}</small>
        </div>
        <span className={`mark${state.live ? "" : " dim"}`} aria-hidden="true" />
      </div>
      {state.updated && (
        <button className="update-bar" type="button" onClick={() => location.reload()}>
          Agora was updated. Reload to get the new version.
        </button>
      )}
      <nav className="side-nav">
        <div className="rh">
          <span>Rooms</span>
          {!guest && (
            <button className="add" type="button" onClick={() => setModal("room")} aria-label="New room" title="New room">
              +
            </button>
          )}
        </div>
        {joined.length === 0 && <div className="empty">No rooms yet</div>}
        {joined.map((r) => (
          <RoomLink key={r.id} room={r} label={r.name} />
        ))}
        {browsable.length > 0 && (
          <button className="item muted" type="button" onClick={() => setModal("browse")}>
            Browse {browsable.length} more {browsable.length === 1 ? "room" : "rooms"}
          </button>
        )}

        {(boot.projects.length > 0 || boot.workspace.role === "admin") && (
          <>
            <div className="rh">
              <span>Projects</span>
              {boot.workspace.role === "admin" && (
                <button className="add" type="button" onClick={() => setModal("project")} aria-label="New project" title="New project">
                  +
                </button>
              )}
            </div>
            {boot.projects.filter((p) => p.status !== "archived").length === 0 && <div className="empty">No projects yet</div>}
            {boot.projects
              .filter((p) => p.status !== "archived")
              .map((p) => (
                <NavLink key={p.id} className="item" to={`/p/${p.id}`}>
                  <span className="project-mark" aria-hidden="true" />
                  <span className="label">{p.name}</span>
                  {p.status === "paused" && <span className="chip">paused</span>}
                </NavLink>
              ))}
          </>
        )}

        <div className="rh">
          <span>Direct messages</span>
          <button className="add" type="button" onClick={() => setModal("dm")} aria-label="New message" title="New message">
            +
          </button>
        </div>
        {dms.length === 0 && <div className="empty">No messages yet</div>}
        {dms.map((r) => (
          <RoomLink key={r.id} room={r} label={roomTitle(state, r)} />
        ))}

        <div className="rh">
          <span>Work</span>
        </div>
        <NavLink className="item" to="/tasks">
          Tasks
        </NavLink>
        <NavLink className="item" to="/office">
          Office
        </NavLink>
        <button className="item" type="button" onClick={onHelp} title="Help for this page (press ?)">
          Help <span className="kbd">?</span>
        </button>
        {boot.workspace.role === "admin" && (
          <>
            <NavLink className="item" to="/settings/usage">
              Usage and spending
            </NavLink>
            <NavLink className="item" to="/settings" end>
              Settings
            </NavLink>
          </>
        )}
      </nav>
      <div className="me">
        <NavLink to="/profile" className="me-link" title="Your profile">
          <Avatar person={{ ...boot.me, kind: "human" }} size={30} />
          <div className="who">
            <b>{boot.me.name}</b>
            <small>{boot.me.email}</small>
          </div>
        </NavLink>
        <button
          className="linkbtn"
          type="button"
          onClick={async () => {
            await signOut();
            navigate("/sign-in", { replace: true });
          }}
        >
          Sign out
        </button>
      </div>
      {modal === "room" && <NewRoomModal onClose={() => setModal(null)} />}
      {modal === "dm" && <NewDmModal onClose={() => setModal(null)} />}
      {modal === "browse" && <BrowseModal rooms={browsable} onClose={() => setModal(null)} />}
      {modal === "project" && <NewProjectModal onClose={() => setModal(null)} />}
    </aside>
  );
}

function RoomLink({ room, label }: { room: Room; label: string }) {
  const person = useStore((s) => (room.dmWith ? s.boot?.people.find((p) => p.id === room.dmWith) : undefined));
  return (
    <NavLink className={`item${room.unread ? " unread" : ""}`} to={`/r/${room.id}`}>
      {room.kind === "dm" && person ? <Avatar person={person} size={20} showOnline /> : <RoomDot color={room.color} />}
      <span className="label">{label}</span>
      {room.isPrivate && room.kind === "room" && <Lock />}
      <Badge count={room.mentions || room.unread} mention={room.mentions > 0} />
    </NavLink>
  );
}

export function NewRoomModal({ onClose }: { onClose: () => void }) {
  const colors = useStore((s) => s.boot?.colors ?? EMPTY);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [color, setColor] = useState(colors[0] ?? "#56632a");
  const [isPrivate, setPrivate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await createRoom({ name, purpose, color, isPrivate });
      onClose();
      navigate(`/r/${id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
      setBusy(false);
    }
  }

  return (
    <Modal title="New room" onClose={onClose}>
      <form onSubmit={submit} className="form">
        <ErrorNote error={error} />
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required placeholder="For example Marketing" />
        </label>
        <label className="field">
          <span>Purpose</span>
          <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={300} rows={2} placeholder="What this room is for" />
        </label>
        <fieldset className="field">
          <legend>Color</legend>
          <div className="swatches">
            {colors.map((c) => (
              <button key={c} type="button" className={`swatch${c === color ? " on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Color ${c}`} aria-pressed={c === color} />
            ))}
          </div>
        </fieldset>
        <label className="check">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} />
          <span>
            Private room
            <small>Only people you add can see it.</small>
          </span>
        </label>
        <div className="actions">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
            Create room
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NewDmModal({ onClose }: { onClose: () => void }) {
  const boot = useStore((s) => s.boot)!;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const others = useMemo(
    () =>
      boot.people
        .filter((p) => p.id !== boot.me.id && p.kind === "human")
        .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.email ?? "").includes(q.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [boot, q],
  );
  return (
    <Modal title="New message" onClose={onClose}>
      <div className="form">
        <label className="field">
          <span>Find someone</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" />
        </label>
        <div className="picklist">
          {others.length === 0 && <p className="muted-text">Nobody else is here yet. Invite your team from Settings.</p>}
          {others.map((p) => (
            <button
              key={p.id}
              className="pick"
              type="button"
              onClick={async () => {
                const id = await openDm(p.id);
                onClose();
                navigate(`/r/${id}`);
              }}
            >
              <Avatar person={p} size={28} showOnline />
              <span>
                {p.name}
                {p.email && <small>{p.email}</small>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function BrowseModal({ rooms, onClose }: { rooms: Room[]; onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <Modal title="Browse rooms" onClose={onClose}>
      <div className="picklist">
        {rooms.map((r) => (
          <div key={r.id} className="pick static">
            <RoomDot color={r.color} />
            <span>
              {r.name} {r.isPrivate && <Lock />}
              <small>{r.purpose || `${r.members.length} ${r.members.length === 1 ? "member" : "members"}`}</small>
            </span>
            <button className="btn ghost small" type="button" onClick={() => (onClose(), navigate(`/r/${r.id}`))}>
              Open
            </button>
            {r.canJoin && (
              <button
                className="btn primary small"
                type="button"
                onClick={async () => {
                  await joinRoom(r.id);
                  onClose();
                  navigate(`/r/${r.id}`);
                }}
              >
                Join
              </button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

/** "/" goes to the room with the newest activity, or a welcome page when there is none. */
export function HomeRedirect() {
  const boot = useStore((s) => s.boot)!;
  const [modal, setModal] = useState(false);
  const mine = boot.rooms.filter((r) => r.isMember).sort((a, b) => b.lastMessageId - a.lastMessageId);
  if (mine[0]) return <Navigate to={`/r/${mine[0].id}`} replace />;
  const guest = boot.workspace.role === "guest";
  return (
    <main className="welcome">
      <h1>Welcome, {boot.me.name.split(/\s+/)[0]}</h1>
      <p>{guest ? "You will see the rooms you are invited to here." : "Rooms are where your team talks. Start with one."}</p>
      {!guest && (
        <div className="row-actions">
          <button className="btn primary" type="button" onClick={() => setModal(true)}>
            Create a room
          </button>
          {boot.workspace.role === "admin" && (
            <NavLink className="btn ghost" to="/settings">
              Invite people
            </NavLink>
          )}
        </div>
      )}
      {modal && <NewRoomModal onClose={() => setModal(false)} />}
    </main>
  );
}
